/**
 * upload/skinStore.ts — the per-browser APP SKIN toggle.
 *
 * Two skins ship:
 *   'v1' — the backend team's original CSS/React. Kept as a FEATURE-INVENTORY
 *          reference: it shows which sections of the product exist. Under a global Preflight its
 *          browser-default typography is gone, so V1 renders flat (body-size headings, collapsed
 *          paragraph spacing, no list markers). That is ACCEPTED, not a bug to fix — V1 is read for
 *          structure, not appearance, and no compat layer is provided.
 *   'v2' — the design system (`tools/upload/design/*` + `design-system.css`). The real reset, the
 *          real tokens, the designer's own components, composed into pages BY HAND
 *          (`UploadOverlayReskin.tsx`).
 *   'v3' — the SAME design system, but the pages are the designer's OWN FILES, copied verbatim from
 *          `~/FrontendFinal/Design/src/pages/*` into `tools/upload/pages/*` and wired to live data.
 *          This is the DEFAULT: it is the design as drawn, wired to live data, so a fresh visitor
 *          sees the intended product. V2 and V3 differ only in who wrote the page layout: V2 is a
 *          re-interpretation of those files, V3 is the files. V2 is kept so the two can be compared
 *          side by side, which is the whole reason V3 is a third option rather than a replacement.
 *
 * SCOPE CHANGED. This used to select between two UPLOAD OVERLAY looks only; it now selects the
 * app's skin, because the design system's Preflight and tokens are global. The storage key is
 * bumped accordingly — the old key holds the string 'reskin', which this type can no longer parse,
 * so a bumped key avoids silently inheriting an unreadable value. One consequence: anyone who had
 * selected the old reskin lands on the default (now V3) once and re-picks if they want something else.
 *
 * This is a tiny module store exposed to React through `useSyncExternalStore` — the idiomatic React 18
 * way to subscribe to an external value with no context (this app has none). A `window` `storage`
 * listener keeps two tabs in sync. The `storage()` wrapper is the SAME defensive one draftStore.ts
 * uses: merely touching localStorage throws in some privacy modes, so access is probed and treated as
 * "unavailable" (→ the default skin, best-effort writes) rather than allowed to crash the page.
 */
import { useSyncExternalStore } from 'react';

export type Skin = 'v1' | 'v2' | 'v3';

/** Bumped from `tb.uploadSkin.v1` when the toggle stopped being upload-only (values changed from
 *  'classic'/'reskin' to 'v1'/'v2'). Bumped again from `tb.skin.v2` to `tb.skin.v3` so any browser
 *  holding the old key's value of 'v1' (the original unstyled classic, kept as a reference) starts
 *  fresh on V2 — the design-system skin. V2 is now the default. */
const KEY = 'tb.skin.v3';
const DEFAULT_SKIN: Skin = 'v2';

/**
 * Parse a stored string into a Skin, falling back to the default on anything unrecognised (including
 * any pre-bump value written by an older build). All three values are accepted so an explicit stored
 * choice — including 'v1' for developers who want the reference look — is always honoured.
 */
function parseSkin(raw: string | null): Skin {
  return raw === 'v1' || raw === 'v2' || raw === 'v3' ? raw : DEFAULT_SKIN;
}

/**
 * localStorage, or null when it cannot be used. Copied from draftStore.ts on purpose: a shared helper
 * would couple the skin toggle to the draft module for no benefit, and this must degrade to "toggle
 * still works in-memory, defaults to the default skin" rather than throw in a privacy-mode browser.
 */
function storage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const probe = `${KEY}:__probe__`;
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return window.localStorage;
  } catch {
    return null;
  }
}

/** In-memory mirror so a privacy-mode browser (no storage) still toggles for the current session. */
let current: Skin = readInitial();
const listeners = new Set<() => void>();

function readInitial(): Skin {
  const store = storage();
  if (!store) return DEFAULT_SKIN;
  try {
    return parseSkin(store.getItem(KEY));
  } catch {
    return DEFAULT_SKIN;
  }
}

export function getSkin(): Skin {
  return current;
}

export function setSkin(next: Skin): void {
  if (next === current) return;
  current = next;
  const store = storage();
  if (store) {
    try {
      store.setItem(KEY, next);
    } catch {
      // Best-effort: the in-memory value still drives this session.
    }
  }
  for (const cb of listeners) cb();
}

export function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  // Cross-tab sync: another tab writing the key fires a `storage` event here.
  function onStorage(e: StorageEvent) {
    if (e.key !== KEY) return;
    const next = parseSkin(e.newValue);
    if (next !== current) {
      current = next;
      for (const l of listeners) l();
    }
  }
  if (typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(cb);
    if (typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

/** React hook: the current skin, re-rendering on change (including cross-tab). */
export function useSkin(): Skin {
  return useSyncExternalStore(subscribe, getSkin, () => DEFAULT_SKIN);
}
